/*
  biit (B-068): "Lagt i handlekurven" drawer with accessories that fit.

  Empire sends `cartcount:update` on window after every successful add to cart (product grid,
  product page, quick buy) and after quantity changes on the cart page. We compare /cart.js with the
  previous snapshot to find the line that grew, so nothing depends on Empire's internals. The cart
  line and subtotal come from sections/biit-atc-cart.liquid and the accessories from
  sections/biit-atc-accessories.liquid (Shopify's complementary recommendations, filled by
  bin/recommend), so prices are formatted by the theme like everywhere else.

  Desktop and tablet: side drawer. Phones (< 720 px): bottom sheet, drag down to close.
  Closes with x, Esc, the overlay, "Fortsett å handle" and the browser's back button.
*/
(() => {
  'use strict';

  const root = document.querySelector('[data-biit-atc]');
  if (!root) return;

  const panel = root.querySelector('[data-biit-atc-panel]');
  const body = root.querySelector('[data-biit-atc-body]');
  const itemBox = root.querySelector('[data-biit-atc-item]');
  const accBox = root.querySelector('[data-biit-atc-accessories]');
  const list = root.querySelector('[data-biit-atc-list]');
  const subtotalBox = root.querySelector('[data-biit-atc-subtotal]');
  const base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const onCartPage = /\/cart\/?$/.test(window.location.pathname);
  const phone = window.matchMedia('(max-width: 719px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  let snapshot = null; // Map of line key -> quantity
  let selfUpdate = false; // true while we send cartcount:update ourselves
  let opener = null;
  let historyPushed = false;
  let isOpen = false;
  let openToken = 0;

  const json = { Accept: 'application/json' };
  const getCart = () => fetch(`${base}cart.js`, { credentials: 'same-origin', headers: json }).then((r) => r.json());
  const toMap = (cart) => new Map(cart.items.map((i) => [i.key, i.quantity]));
  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
  const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  getCart().then((c) => { snapshot = toMap(c); }).catch(() => {});

  // The add-to-cart button the customer used, to give focus back when the drawer closes
  // (Safari does not focus buttons on click, so document.activeElement is not enough).
  // The variant it added tells us which line to show, even when the cart changed elsewhere
  // (another tab) and the snapshot is stale.
  let lastTrigger = null;
  let lastVariant = null; // { id, at }
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-quick-buy], [data-product-atc], .product-form--atc-button');
    if (!t) return;
    lastTrigger = t;
    const form = t.closest('form');
    const id = t.dataset.variantId || (form && form.querySelector('[name="id"]') && form.querySelector('[name="id"]').value);
    lastVariant = id ? { id: Number(id), at: Date.now() } : null;
  }, true);

  window.addEventListener('cartcount:update', async () => {
    if (selfUpdate) return;
    let cart;
    try {
      cart = await getCart();
    } catch (e) {
      return;
    }
    const before = snapshot || new Map();
    snapshot = toMap(cart);
    if (onCartPage) {
      refreshCartPayment();
      return;
    }
    // The variant the customer just clicked, if it was within the last 15 seconds; otherwise the
    // line that grew (Shopify lists the most recently added line first).
    const recent = lastVariant && Date.now() - lastVariant.at < 15000 ? lastVariant.id : null;
    lastVariant = null;
    const added = (recent && cart.items.find((i) => i.variant_id === recent))
      || cart.items.find((i) => i.quantity > (before.get(i.key) || 0));
    if (added) openFor(added, cart);
  });

  // Cart page: the Klarna example follows the cart total (sections/biit-pay-cart.liquid, B-076).
  async function refreshCartPayment() {
    const box = document.querySelector('[data-biit-pay-cart]');
    if (!box) return;
    try {
      const r = await fetch(`${base}?sections=biit-pay-cart`, { credentials: 'same-origin', headers: json });
      const html = (await r.json())['biit-pay-cart'] || '';
      const fresh = parse(html).querySelector('[data-biit-pay]');
      if (fresh) box.replaceChildren(fresh);
      else box.replaceChildren();
    } catch (e) { /* keep the old figures */ }
  }

  async function fetchCartSection() {
    const r = await fetch(`${base}?sections=biit-atc-cart`, { credentials: 'same-origin', headers: json });
    const data = await r.json();
    return parse(data['biit-atc-cart'] || '');
  }

  async function renderCart(key) {
    const doc = await fetchCartSection();
    const line = key ? doc.querySelector(`.biit-atc-line[data-key="${CSS.escape(key)}"]`) : null;
    if (line) itemBox.replaceChildren(line);
    const sub = doc.querySelector('[data-biit-atc-subtotal-source]');
    if (sub) subtotalBox.replaceChildren(sub);
  }

  async function renderAccessories(productId, cart, token) {
    accBox.hidden = false;
    list.innerHTML = '<div class="biit-acc biit-acc--skeleton" aria-hidden="true"><span></span><span></span></div>';
    let doc;
    try {
      const url = `${base}recommendations/products?product_id=${encodeURIComponent(productId)}&limit=8&intent=complementary&section_id=biit-atc-accessories`;
      const r = await fetch(url, { credentials: 'same-origin' });
      doc = parse(await r.text());
    } catch (e) {
      doc = null;
    }
    if (token !== openToken) return;
    const ul = doc && doc.querySelector('.biit-acc-list');
    if (!ul || !ul.children.length) {
      accBox.hidden = true;
      list.replaceChildren();
      return;
    }
    const inCart = new Set(cart.items.map((i) => String(i.product_id)));
    ul.querySelectorAll('.biit-acc').forEach((li) => {
      if (inCart.has(li.dataset.productId)) markAdded(li.querySelector('[data-biit-atc-add]'), 'I handlekurven');
    });
    list.replaceChildren(ul);
  }

  function openFor(item, cart) {
    const token = ++openToken;
    // Show the product at once from cart.js; the formatted line replaces it a moment later.
    itemBox.innerHTML = `
      <div class="biit-atc-line">
        <span class="biit-atc-line__image">${item.image ? `<img src="${escapeHtml(item.image)}" alt="" width="72" height="72">` : ''}</span>
        <div class="biit-atc-line__info"><span class="biit-atc-line__title">${escapeHtml(item.product_title)}</span></div>
      </div>`;
    subtotalBox.replaceChildren();
    show();
    renderCart(item.key).catch(() => {});
    renderAccessories(item.product_id, cart, token);
  }

  // ---------- Adding an accessory ----------
  function markAdded(btn, label) {
    if (!btn) return;
    btn.disabled = true;
    btn.classList.remove('is-busy');
    btn.classList.add('is-added');
    btn.querySelector('.biit-acc__add-text').textContent = label;
  }

  list.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-biit-atc-add]');
    if (!btn || btn.disabled) return;
    btn.disabled = true;
    btn.classList.add('is-busy');
    const text = btn.querySelector('.biit-acc__add-text');
    try {
      const r = await fetch(`${base}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(btn.dataset.biitAtcAdd), quantity: 1 }] }),
      });
      if (!r.ok) throw new Error('add failed');
      markAdded(btn, 'Lagt til ✓');
      const cart = await getCart();
      snapshot = toMap(cart);
      selfUpdate = true;
      window.dispatchEvent(new CustomEvent('cartcount:update', { detail: cart })); // header count
      selfUpdate = false;
      renderCart(null).catch(() => {});
    } catch (err) {
      btn.disabled = false;
      btn.classList.remove('is-busy');
      text.textContent = 'Prøv igjen';
    }
  });

  // ---------- Add buttons outside the drawer (B-076) ----------
  // [data-biit-add="<variant id>"]: the accessory cards on the product page (mode "inline": the
  // button says it was added, the header count updates, no drawer) and the "Nylig sett på" cards
  // (default: the drawer opens, as for every other product card).
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-biit-add]');
    if (!btn || btn.disabled || root.contains(btn)) return;
    e.preventDefault();
    const id = Number(btn.dataset.biitAdd);
    const inline = btn.dataset.biitAddMode === 'inline';
    const text = btn.querySelector('[data-biit-add-text]');
    const label = text ? text.textContent : '';
    btn.disabled = true;
    btn.classList.add('is-busy');
    try {
      const r = await fetch(`${base}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity: 1 }] }),
      });
      if (!r.ok) throw new Error('add failed');
      const cart = await getCart();
      btn.classList.remove('is-busy');
      if (inline) {
        btn.classList.add('is-added');
        if (text) text.textContent = 'Lagt til ✓';
        snapshot = toMap(cart);
        selfUpdate = true;
        window.dispatchEvent(new CustomEvent('cartcount:update', { detail: cart })); // header count
        selfUpdate = false;
      } else {
        btn.disabled = false;
        lastTrigger = btn;
        lastVariant = { id, at: Date.now() };
        window.dispatchEvent(new CustomEvent('cartcount:update', { detail: cart })); // opens the drawer
      }
    } catch (err) {
      btn.disabled = false;
      btn.classList.remove('is-busy');
      if (text) text.textContent = 'Prøv igjen';
      window.setTimeout(() => { if (text && !btn.classList.contains('is-added')) text.textContent = label; }, 3000);
    }
  });

  // ---------- Open and close ----------
  function show() {
    if (isOpen) {
      body.scrollTop = 0;
      return;
    }
    isOpen = true;
    opener = lastTrigger && document.contains(lastTrigger) ? lastTrigger : document.activeElement;
    root.hidden = false;
    document.documentElement.classList.add('biit-atc-lock');
    panel.style.transform = '';
    // Next frame, so the transition runs from the closed position. The timeout is a fallback for
    // tabs where animation frames are paused (background tabs), so the drawer always opens.
    const openNow = () => { if (isOpen) root.classList.add('is-open'); };
    requestAnimationFrame(() => requestAnimationFrame(openNow));
    window.setTimeout(openNow, 80);
    body.scrollTop = 0;
    panel.focus({ preventScroll: true });
    try {
      window.history.pushState({ biitAtc: true }, '');
      historyPushed = true;
    } catch (e) {
      historyPushed = false;
    }
  }

  function close(fromHistory) {
    if (!isOpen) return;
    isOpen = false;
    openToken++;
    panel.style.transform = ''; // after a drag, let the closed position in CSS take over
    root.classList.remove('is-open');
    document.documentElement.classList.remove('biit-atc-lock');
    const done = () => {
      if (!isOpen) root.hidden = true;
    };
    if (reducedMotion.matches) done();
    else window.setTimeout(done, 300);
    if (opener && typeof opener.focus === 'function' && document.contains(opener)) opener.focus({ preventScroll: true });
    if (historyPushed && !fromHistory) {
      historyPushed = false;
      window.history.back();
    }
    historyPushed = false;
  }

  root.addEventListener('click', (e) => {
    // Keep clicks inside the drawer away from Empire's document listener, which would otherwise
    // close its (hidden) banner a second time and throw.
    e.stopPropagation();
    if (e.target.closest('[data-biit-atc-close]')) close(false);
  });

  window.addEventListener('popstate', () => {
    if (isOpen) close(true);
  });

  // Leaving for the cart: drop our history entry quietly so Back from the cart works as expected.
  root.querySelector('.biit-atc__to-cart').addEventListener('click', () => {
    historyPushed = false;
  });

  // ---------- Keyboard: Esc and a focus trap ----------
  root.addEventListener('keydown', (e) => {
    if (!isOpen) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      close(false);
      return;
    }
    if (e.key !== 'Tab') return;
    const focusable = [...panel.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')]
      .filter((el) => el.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });

  // ---------- Phones: drag the sheet down to close ----------
  let dragStart = null;
  let dragDelta = 0;
  const dragZone = root.querySelectorAll('[data-biit-atc-grip], [data-biit-atc-drag]');
  dragZone.forEach((zone) => {
    zone.addEventListener('touchstart', (e) => {
      if (!phone.matches || e.target.closest('button')) return;
      dragStart = e.touches[0].clientY;
      dragDelta = 0;
      panel.classList.add('is-dragging');
    }, { passive: true });
  });
  window.addEventListener('touchmove', (e) => {
    if (dragStart === null) return;
    dragDelta = Math.max(0, e.touches[0].clientY - dragStart);
    panel.style.transform = `translateY(${dragDelta}px)`;
  }, { passive: true });
  window.addEventListener('touchend', () => {
    if (dragStart === null) return;
    panel.classList.remove('is-dragging');
    dragStart = null;
    if (dragDelta > 90) close(false);
    else panel.style.transform = '';
  });
})();
